import React, { useEffect, useMemo, useState } from 'react';
import AudioPlayer from '../AudioPlayer';
import './setthebar.css';
import setTheBarTheme from '../../sound/setthebar/theme.mp3';

const sortScoreboard = (scoreboard = []) => [...scoreboard].sort((a, b) => b.iq - a.iq);

const formatIq = (value) => {
  const rounded = Number(value || 0);
  if (Math.abs(rounded - Math.round(rounded)) < 0.01) {
    return String(Math.round(rounded));
  }
  return rounded.toFixed(1);
};

export default function SetTheBarPlayer({ socket, sessionId, playerName, isMusicMuted = false }) {
  const [state, setState] = useState({
    phase: 'lobby',
    currentQuestion: null,
    review: null,
    scoreboard: [],
    progress: { intermissionEndsAt: null },
  });
  const [myVoteChoice, setMyVoteChoice] = useState(null);
  const [timeLeftMs, setTimeLeftMs] = useState(0);

  useEffect(() => {
    if (!socket) return undefined;

    const onState = (incoming) => {
      setState((prev) => ({ ...prev, ...incoming }));
    };

    socket.on('setTheBarState', onState);
    socket.emit('setTheBarSyncState', { sessionId });

    return () => {
      socket.off('setTheBarState', onState);
    };
  }, [socket, sessionId]);

  useEffect(() => {
    setMyVoteChoice(null);
  }, [state.review?.currentEntry?.entryId]);

  useEffect(() => {
    const targetEndsAt = state.phase === 'question'
      ? state.currentQuestion?.endsAt
      : state.phase === 'review-vote'
        ? state.review?.voteEndsAt
        : state.phase === 'intermission'
          ? state.progress?.intermissionEndsAt
          : null;

    if (!targetEndsAt) {
      setTimeLeftMs(0);
      return undefined;
    }

    const tick = () => {
      setTimeLeftMs(Math.max(targetEndsAt - Date.now(), 0));
    };

    tick();
    const intervalId = setInterval(tick, 100);
    return () => clearInterval(intervalId);
  }, [state.phase, state.currentQuestion?.endsAt, state.review?.voteEndsAt, state.progress?.intermissionEndsAt]);

  const scoreboard = useMemo(() => sortScoreboard(state.scoreboard), [state.scoreboard]);
  const isThemePlaying = state.phase !== 'lobby' && state.phase !== 'leaderboard';
  const me = useMemo(
    () => (state.scoreboard || []).find((entry) => entry.name === playerName),
    [state.scoreboard, playerName]
  );

  const myId = me?.id;
  const myAnswered = Boolean(
    myId
    && state.currentQuestion?.answeredPlayerIds
    && state.currentQuestion.answeredPlayerIds.includes(myId)
  );

  const myVoteSubmitted = Boolean(
    myId
    && state.review?.voteProgress?.votedVoterIds
    && state.review.voteProgress.votedVoterIds.includes(myId)
  );

  const submitAnswer = (answerId) => {
    if (myAnswered) return;
    socket.emit('setTheBarSubmitAnswer', { sessionId, answerId });
  };

  const submitVote = (guessedPlayerId) => {
    if (myVoteSubmitted) return;
    socket.emit('setTheBarVoteOnWrong', { sessionId, guessedPlayerId });
    setMyVoteChoice(guessedPlayerId);
  };

  return (
    <div className="stb-player-shell">
      <AudioPlayer audioSrc={setTheBarTheme} isPlaying={isThemePlaying} isMuted={isMusicMuted} />
      <div className="stb-player-card">
        <div className="stb-player-top">
          <span>{playerName || 'Player'}</span>
          <strong>{formatIq(me?.iq || 0)} / 160 IQ</strong>
        </div>

        {state.phase === 'lobby' && (
          <div className="stb-player-section">
            <h2>Set The Bar</h2>
            <p>Host is setting up the match.</p>
            <p>Answer fast, then sniff out who wrote each wrong answer.</p>
          </div>
        )}

        {state.phase === 'question' && state.currentQuestion && (
          <div className="stb-player-section">
            <div className="stb-player-chip-row">
              <span className="stb-pill">Q{state.currentQuestion.index}/{state.currentQuestion.total}</span>
              <span className="stb-pill">{(timeLeftMs / 1000).toFixed(1)}s</span>
            </div>

            <h3>{state.currentQuestion.prompt}</h3>

            <div className="stb-player-option-grid">
              {(state.currentQuestion.options || []).map((option) => (
                <button
                  key={option.id}
                  className={`stb-player-option ${myAnswered ? 'locked' : ''}`}
                  onClick={() => submitAnswer(option.id)}
                  disabled={myAnswered}
                >
                  <span>{option.id}</span>
                  <strong>{option.text}</strong>
                </button>
              ))}
            </div>

            <p className="stb-player-note">
              {myAnswered
                ? 'Answer locked. Wait for reveals.'
                : 'Tap one option to lock in. No answer means no score and no detective vote for your miss.'}
            </p>
          </div>
        )}

        {state.phase === 'intermission' && (
          <div className="stb-player-section stb-center">
            <h3>Next question...</h3>
            <p>{(timeLeftMs / 1000).toFixed(1)}s</p>
          </div>
        )}

        {state.phase === 'review-stats' && state.review && (
          <div className="stb-player-section">
            <h3>Question {state.review.questionIndex} Stats</h3>
            <p className="stb-player-note">{state.review.questionPrompt}</p>
            <div className="stb-player-stat-grid">
              <div className="stb-stat-card success">
                <h4>{state.review.stats?.percentRight || 0}%</h4>
                <p>Right</p>
              </div>
              <div className="stb-stat-card danger">
                <h4>{state.review.stats?.percentWrong || 0}%</h4>
                <p>Wrong</p>
              </div>
            </div>
          </div>
        )}

        {state.phase === 'review-vote' && state.review?.currentEntry && (
          <div className="stb-player-section">
            <div className="stb-player-chip-row">
              <span className="stb-pill">Detective Vote</span>
              <span className="stb-pill">{(timeLeftMs / 1000).toFixed(1)}s</span>
            </div>

            <p className="stb-anon-answer">“{state.review.currentEntry.answerText}”</p>
            <p className="stb-player-note">Who do you think submitted this wrong answer?</p>

            <div className="stb-player-vote-grid">
              {scoreboard.map((entry) => (
                <button
                  key={entry.id}
                  className={`stb-player-vote ${myVoteChoice === entry.id ? 'selected' : ''}`}
                  onClick={() => submitVote(entry.id)}
                  disabled={myVoteSubmitted}
                >
                  {entry.name}
                </button>
              ))}
            </div>

            <p className="stb-player-note">
              Votes in: {state.review.voteProgress?.submittedVotes || 0} / {state.review.voteProgress?.totalVoters || 0}
            </p>
          </div>
        )}

        {state.phase === 'review-reveal' && state.review?.lastReveal && (
          <div className="stb-player-section stb-reveal">
            <h3>Reveal</h3>
            <p className="stb-player-note">Wrong answer: “{state.review.lastReveal.answerText}”</p>
            <p><strong>{state.review.lastReveal.accusedPlayerName}</strong> wrote it.</p>
            <p>
              Refund: +{formatIq(state.review.lastReveal.refundPoints)} IQ ({Math.round((state.review.lastReveal.refundRatio || 0) * 100)}%)
            </p>
          </div>
        )}

        {state.phase === 'review-correct' && state.review && (
          <div className="stb-player-section stb-center">
            <h3>Correct Answer</h3>
            <p className="stb-correct">{state.review.correctAnswerText}</p>
          </div>
        )}

        {state.phase === 'leaderboard' && (
          <div className="stb-player-section">
            <h3>Final IQ Leaderboard</h3>
            {scoreboard.map((entry, index) => (
              <div key={entry.id} className="stb-player-rank-row">
                <span>#{index + 1} {entry.name}</span>
                <strong>{formatIq(entry.iq)} / 160</strong>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
